import type { ApexOptions } from 'apexcharts';
import type { Theme } from '@mui/material/styles';

import { alpha } from '@mui/material/styles';

/**
 * Chart palette derived entirely from the theme's semantic colors so charts
 * stay in lockstep with the design system. Ordered so adjacent series contrast.
 */
export function chartPalette(theme: Theme): string[] {
  const p = theme.palette;
  return [
    p.primary.main,
    p.info.main,
    p.warning.main,
    p.secondary.dark,
    p.error.main,
    p.primary.dark,
    p.info.dark,
    p.warning.dark,
  ];
}

export function chartCardSx(theme: Theme) {
  const ring = alpha(theme.palette.common.white, 0.08);
  const ringHover = alpha(theme.palette.common.white, 0.13);
  const ambient = alpha(theme.palette.common.black, 0.4);

  return {
    width: '100%',
    height: '60vh',
    minHeight: 360,
    p: { xs: 1.5, sm: 2 },
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
    boxSizing: 'border-box',
    bgcolor: theme.palette.container?.medium ?? theme.palette.grey[800],
    color: theme.palette.common.white,
    border: 'none',
    borderRadius: 2,
    fontVariantNumeric: 'tabular-nums',
    boxShadow: `inset 0 0 0 1px ${ring}, 0 10px 24px -12px ${ambient}`,
    transitionProperty: 'box-shadow',
    transitionDuration: '200ms',
    transitionTimingFunction: 'cubic-bezier(0.23, 1, 0.32, 1)',
    '&:hover': {
      boxShadow: `inset 0 0 0 1px ${ringHover}, 0 14px 28px -12px ${ambient}`,
    },
    opacity: 0,
    transform: 'translateY(8px)',
    animation: 'ari-chart-enter 240ms cubic-bezier(0.23, 1, 0.32, 1) forwards',
    '@keyframes ari-chart-enter': {
      to: { opacity: 1, transform: 'translateY(0)' },
    },
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
      opacity: 1,
      transform: 'none',
    },
  } as const;
}

export function chartTitleSx(theme: Theme) {
  return {
    color: theme.palette.common.white,
    textWrap: 'balance' as const,
    letterSpacing: '-0.01em',
    fontWeight: 600,
  };
}

export function baseChartOptions(theme: Theme, reduceMotion: boolean): Partial<ApexOptions> {
  const label = theme.palette.common.white;
  const grid = alpha(theme.palette.common.white, 0.08);
  const fontFamily = (theme.typography.fontFamily as string | undefined) ?? 'Inter, sans-serif';

  return {
    chart: {
      toolbar: { show: false },
      zoom: { enabled: false },
      foreColor: label,
      background: 'transparent',
      fontFamily,
      animations: reduceMotion
        ? { enabled: false }
        : { enabled: true, speed: 220, animateGradually: { enabled: false } },
    },
    dataLabels: { enabled: false },
    grid: {
      borderColor: grid,
      strokeDashArray: 3,
      xaxis: { lines: { show: false } },
      padding: { left: 4, right: 4, top: 0, bottom: 0 },
    },
    tooltip: {
      theme: 'dark',
      shared: true,
      intersect: false,
      style: { fontFamily, fontSize: '12px' },
    },
    states: {
      hover: { filter: { type: 'lighten' } },
      active: { filter: { type: 'darken' } },
    },
  };
}

interface AxisConfig {
  categories?: string[];
  label?: string | null;
  forceNiceScale?: boolean;
}

export function buildAxis(theme: Theme, { categories, label, forceNiceScale }: AxisConfig): Record<string, unknown> {
  const color = theme.palette.common.white;
  const fontFamily = (theme.typography.fontFamily as string | undefined) ?? 'Inter, sans-serif';

  const axis: Record<string, unknown> = {
    axisBorder: { show: false },
    axisTicks: { show: false },
    labels: {
      style: { colors: color, fontSize: '12px', fontFamily, fontWeight: 500 },
    },
  };

  if (categories) axis.categories = categories;
  if (forceNiceScale) axis.forceNiceScale = true;
  if (label) {
    axis.title = {
      text: label,
      style: { color, fontSize: '11px', fontWeight: 500, fontFamily },
    };
  }
  return axis;
}
